import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { ApolloQueryResult } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { useBoolean } from 'src/hooks/use-boolean';

import {
  NuvemshopStoreIntegrationType,
  useNuvemshopAuthenticationMutation,
} from 'src/graphql/generated';

import Image from 'src/components/image';
import { RHFMultiCheckbox } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';

import {
  NuvemshopFormSchema,
  nuvemshopConfigFormSchemaDefaultValues,
} from '../schema/nuvemshop-form-schema';

// ----------------------------------------------------------------------

type TCurrentData = Pick<NuvemshopStoreIntegrationType, 'connected'>;

type Props = {
  open: boolean;
  onClose: () => void;
  currentData?: TCurrentData | null;
  code?: string | null;
  integration?: string | null;
  refetch: () => Promise<ApolloQueryResult<any>>;
};

// ----------------------------------------------------------------------

export function ConfigNuvemshopModal({
  open,
  onClose,
  currentData,
  code,
  refetch,
  integration,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [nuvemshopAuthentication] = useNuvemshopAuthenticationMutation();
  const enableForm = useBoolean();

  const methods = useForm({
    resolver: yupResolver(NuvemshopFormSchema),
    defaultValues: nuvemshopConfigFormSchemaDefaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = methods;

  const handleClose = () => {
    if (!currentData) enableForm.onTrue();
    else enableForm.onFalse();

    onClose();
    reset();
  };

  const onSubmit = handleSubmit(async (data) => {
    let label = 'Cadastro efetuado com sucesso!';

    const variables = {
      input: {
        connected: data.activatedGroup?.includes('activated'),
        code: code || '',
      },
    };

    if (currentData) label = 'Dados atualizados com sucesso!';

    try {
      const response = await nuvemshopAuthentication({ variables });

      if (response.data?.nuvemshopAuthenticationRequest?.ok) {
        enqueueSnackbar(label, { variant: 'success' });

        await refetch();
      }

      enableForm.onFalse();
      onClose();
    } catch (error) {
      enqueueSnackbar('Não foi possível conectar a Nuvemshop. Código expirado ou inválido', {
        variant: 'error',
      });
    }
  });

  useEffect(() => {
    const updateFields = () => {
      if (!currentData) return;

      reset({
        activatedGroup: currentData.connected ? ['activated'] : [],
      });
    };

    updateFields();
  }, [currentData, reset]);

  return (
    <FormProvider methods={methods}>
      <Dialog open={open} onClose={onClose}>
        {code && integration && integration === 'nuvemshop' && (
          <>
            <DialogTitle textAlign="center" fontSize={20}>
              Integração Nuvemshop
            </DialogTitle>

            <DialogContent
              sx={{
                minWidth: { xs: 400, md: 400 },
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <RHFMultiCheckbox
                name="activatedGroup"
                options={[{ value: 'activated', label: 'Ativar integração Nuvemshop' }]}
                disabled={!enableForm}
              />
            </DialogContent>

            <Divider sx={{ borderStyle: 'dashed' }} />

            <DialogActions>
              <Button variant="outlined" color="inherit" onClick={handleClose}>
                Fechar
              </Button>
              <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                loading={isSubmitting}
                onClick={onSubmit}
              >
                Salvar
              </LoadingButton>
            </DialogActions>
          </>
        )}

        {!code && currentData?.connected && (!integration || integration !== 'nuvemshop') && (
          <>
            <DialogTitle textAlign="center" fontSize={20}>
              Integração Nuvemshop
            </DialogTitle>

            <DialogContent
              sx={{
                minWidth: { xs: 400, md: 400 },
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <RHFMultiCheckbox
                name="activatedGroup"
                options={[{ value: 'activated', label: 'Ativar integração Nuvemshop' }]}
                disabled={!enableForm}
              />
            </DialogContent>

            <Divider sx={{ borderStyle: 'dashed' }} />

            <DialogActions>
              <Button variant="outlined" color="inherit" onClick={handleClose}>
                Fechar
              </Button>
              <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                loading={isSubmitting}
                onClick={onSubmit}
              >
                Salvar
              </LoadingButton>
            </DialogActions>
          </>
        )}
        {!code && !currentData?.connected && (
          <>
            <Stack alignItems="center" pt={3}>
              <Image src="/assets/images/ic-duotone.png" width={60} />
            </Stack>
            <DialogTitle textAlign="center" fontSize={20}>
              Integração Nuvemshop
            </DialogTitle>

            <DialogContent
              sx={{
                pb: 4,
                px: 5,
                minWidth: 280,
                display: 'flex',
                justifyContent: 'center',
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              Para integrar sua conta à Nuvemshop, basta acessar o link <br />
              de convite diretamente pelo painel da sua conta na <br />
              Nuvemshop. Caso precise de ajuda, acesse nossa Central de <br />
              Ajuda, onde explicamos detalhadamente como <br />
              localizar e conectar nosso aplicativo à sua conta Nuvemshop.
            </DialogContent>
          </>
        )}
      </Dialog>
    </FormProvider>
  );
}
